/**
 * Stripe Dashboard links for Admin → Orders (uses /api/payment/config stripeDashboard payload).
 */

export function resolveOrderStripeMode(order, { paymentConfig } = {}) {
  const stored = order?.stripe_mode;
  if (stored === 'live' || stored === 'sandbox') return stored;

  const fromPi = inferStripeModeFromPaymentIntentId(order?.payment_intent_id, {
    sandboxAccountId: paymentConfig?.stripeDashboard?.sandbox?.accountId,
    liveAccountId: paymentConfig?.stripeDashboard?.live?.accountId,
  });
  if (fromPi) return fromPi;

  const def = paymentConfig?.defaultStripeMode || paymentConfig?.defaultPaymentMode;
  if (def === 'live' || def === 'sandbox') return def;
  return paymentConfig?.stripeSandbox === false ? 'live' : 'sandbox';
}

function inferStripeModeFromPaymentIntentId(paymentIntentId, { sandboxAccountId, liveAccountId } = {}) {
  const pi = String(paymentIntentId || '');
  if (!pi.startsWith('pi_')) return null;

  const sandboxMatch = accountSlugMatchesPaymentIntent(pi, sandboxAccountId);
  const liveMatch = accountSlugMatchesPaymentIntent(pi, liveAccountId);

  if (sandboxMatch && !liveMatch) return 'sandbox';
  if (liveMatch && !sandboxMatch) return 'live';
  if (liveMatch && sandboxMatch) {
    const sandboxSlug = String(sandboxAccountId || '').replace(/^acct_1/, '');
    const liveSlug = String(liveAccountId || '').replace(/^acct_1/, '');
    if (pi.includes(liveSlug) && !pi.includes(sandboxSlug)) return 'live';
    if (pi.includes(sandboxSlug) && !pi.includes(liveSlug)) return 'sandbox';
  }
  return null;
}

function accountSlugMatchesPaymentIntent(pi, accountId) {
  const slug = String(accountId || '').replace(/^acct_1/, '');
  if (!slug || !pi) return false;
  if (pi.includes(slug)) return true;
  for (let len = Math.min(slug.length, 16); len >= 8; len -= 1) {
    const tail = slug.slice(-len);
    if (pi.includes(tail)) return true;
  }
  return false;
}

export function resolveOrderPayPalMode(order, { paymentConfig } = {}) {
  const stored = order?.paypal_mode;
  if (stored === 'live' || stored === 'sandbox') return stored;

  const fromLabel = inferPayPalModeFromPaymentMethod(order?.payment_method);
  if (fromLabel) return fromLabel;

  const def = paymentConfig?.defaultPayPalMode || paymentConfig?.defaultPaymentMode;
  if (def === 'live' || def === 'sandbox') return def;
  return paymentConfig?.paypalSandbox === false ? 'live' : 'sandbox';
}

function inferPayPalModeFromPaymentMethod(paymentMethod) {
  const pm = String(paymentMethod || '');
  if (/paypal\s*(test|sandbox)/i.test(pm)) return 'sandbox';
  if (/paypal\s*live/i.test(pm)) return 'live';
  return null;
}

export function stripePaymentDetailUrl(paymentIntentId, mode, paymentConfig) {
  const id = String(paymentIntentId || '').trim();
  if (!id) return null;
  const dash = paymentConfig?.stripeDashboard?.[mode === 'live' ? 'live' : 'sandbox'];
  if (dash?.paymentsListUrl) {
    return `${dash.paymentsListUrl}/${encodeURIComponent(id)}`;
  }
  const prefix = mode === 'live' ? 'https://dashboard.stripe.com/payments' : 'https://dashboard.stripe.com/test/payments';
  return `${prefix}/${encodeURIComponent(id)}`;
}

export function stripePaymentsListUrl(mode, paymentConfig) {
  const dash = paymentConfig?.stripeDashboard?.[mode === 'live' ? 'live' : 'sandbox'];
  return dash?.paymentsListUrl || (mode === 'live' ? 'https://dashboard.stripe.com/payments' : 'https://dashboard.stripe.com/test/payments');
}

export function stripeDashboardHomeUrl(mode, paymentConfig) {
  const dash = paymentConfig?.stripeDashboard?.[mode === 'live' ? 'live' : 'sandbox'];
  return dash?.dashboardHomeUrl || (mode === 'live' ? 'https://dashboard.stripe.com' : 'https://dashboard.stripe.com/test');
}

export function isStripePaymentMethod(paymentMethod) {
  return /stripe/i.test(String(paymentMethod || ''));
}

/** Human-readable payment label with Stripe/PayPal Test/Live when detectable. */
export function formatOrderPaymentLabel(order, { paymentConfig } = {}) {
  const base = order?.payment_method || 'Credit Card';

  const isStripe = isStripePaymentMethod(base) || Boolean(order?.payment_intent_id);
  if (isStripe) {
    if (/stripe\s+(test|live)/i.test(base)) return base;
    const mode = resolveOrderStripeMode(order, { paymentConfig });
    if (mode === 'sandbox') return 'Stripe Test (Credit Card)';
    if (mode === 'live') return 'Stripe Live (Credit Card)';
    return base;
  }

  const isPayPal = isPayPalPaymentMethod(base) || Boolean(order?.paypal_order_id);
  if (isPayPal) {
    if (/paypal\s+(test|sandbox|live)/i.test(base)) return base;
    const mode = resolveOrderPayPalMode(order, { paymentConfig });
    if (mode === 'sandbox') return 'PayPal Test';
    if (mode === 'live') return 'PayPal Live';
    return base || 'PayPal';
  }

  return base;
}

export function getOrderStripeEnvBadge(order, { paymentConfig } = {}) {
  const isStripe = isStripePaymentMethod(order?.payment_method) || Boolean(order?.payment_intent_id);
  if (!isStripe) return null;

  const mode = resolveOrderStripeMode(order, { paymentConfig });
  if (mode === 'sandbox') {
    return { mode: 'sandbox', label: '🧪 Test', title: 'Paid in Stripe Test / Sandbox' };
  }
  if (mode === 'live') {
    return { mode: 'live', label: '💳 Live', title: 'Paid in Stripe Live' };
  }
  return null;
}

export function getOrderPayPalEnvBadge(order, { paymentConfig } = {}) {
  const isPayPal = isPayPalPaymentMethod(order?.payment_method) || Boolean(order?.paypal_order_id);
  if (!isPayPal) return null;

  const stored = order?.paypal_mode;
  const mode =
    (stored === 'live' || stored === 'sandbox' ? stored : null) ||
    inferPayPalModeFromPaymentMethod(order?.payment_method) ||
    (order?.paypal_order_id
      ? resolveOrderPayPalMode(order, { paymentConfig })
      : null);

  if (mode === 'sandbox') {
    return { mode: 'sandbox', label: '🧪 Test', title: 'Paid in PayPal Sandbox' };
  }
  if (mode === 'live') {
    return { mode: 'live', label: '🅿️ Live', title: 'Paid in PayPal Live' };
  }
  return null;
}

/** Stripe or PayPal environment badge for Admin → Orders. */
/** Badge colors for Admin → Orders environment labels (Stripe/PayPal/mail). */
export function getOrderEnvBadgeStyles(badge) {
  const isTestLike = badge?.mode === 'sandbox' || badge?.mode === 'test';
  return {
    background: isTestLike ? '#fffbeb' : '#f0fdf4',
    border: isTestLike ? '1px solid #fcd34d' : '1px solid #86efac',
    color: isTestLike ? '#92400e' : '#166534',
  };
}

export function getMailOrderEnvBadge(order) {
  if (!order) return null;

  const stored = order?.mail_order_mode;
  const orderNumber = String(order?.order_number || '');
  const isMail =
    stored === 'live' ||
    stored === 'test' ||
    /^FID-MAIL/i.test(orderNumber);

  if (!isMail) return null;

  const mode =
    (stored === 'live' || stored === 'test' ? stored : null) ||
    (/FID-MAIL-TEST-/i.test(orderNumber) ? 'test' : 'live');

  if (mode === 'test') {
    return { mode: 'test', label: '🧪 Test mail', title: 'Test mail order — practice only' };
  }
  return { mode: 'live-mail', label: '✉️ Live mail', title: 'Live mail / check order' };
}

export function getOrderPaymentEnvBadge(order, opts = {}) {
  return getOrderStripeEnvBadge(order, opts) || getOrderPayPalEnvBadge(order, opts) || getMailOrderEnvBadge(order);
}

/** Dashboard search when no payment_intent_id is stored (imported / legacy orders). */
export function stripeDashboardSearchUrl(query, mode, paymentConfig) {
  const q = String(query || '').trim();
  const home = stripeDashboardHomeUrl(mode, paymentConfig);
  if (!q) return stripePaymentsListUrl(mode, paymentConfig);
  return `${home}/search?query=${encodeURIComponent(q)}`;
}

export function isPayPalPaymentMethod(paymentMethod) {
  return /paypal/i.test(String(paymentMethod || ''));
}

export function paypalTransactionUrl(paypalOrderId, mode) {
  const id = String(paypalOrderId || '').trim();
  if (!id) return null;
  const base = mode === 'live' ? 'https://www.paypal.com' : 'https://www.sandbox.paypal.com';
  return `${base}/activity/payment/${encodeURIComponent(id)}`;
}

/** Activity list when no paypal_order_id is stored (imported / legacy orders). */
export function paypalActivityUrl(mode) {
  return mode === 'live'
    ? 'https://www.paypal.com/activity'
    : 'https://www.sandbox.paypal.com/activity';
}
